import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { getCurrentIdToken, logout as cognitoLogout } from './auth';
import { decodeIdToken, type DecodedClaims } from './jwt';

export type Role = 'admin' | 'abteilungsleiter' | 'uebungsleiter';

interface AuthState {
  loading: boolean;
  idToken?: string;
  claims?: DecodedClaims;
  /** ADR-0004/D-13: eine Person kann mehreren Cognito-Gruppen gleichzeitig angehören. */
  roles: Role[];
  hasRole: (role: Role) => boolean;
  /** ADR-0007/D-32/D-34: nur gesetzt, wenn Vor- und Nachname beide gepflegt sind. */
  fullName?: string;
  /** ADR-0007/D-33: steuert den Hinweis-Banner -- solange false, ist das Profil unvollständig. */
  hasCompleteName: boolean;
  refresh: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

// ADR-0007/D-35: dieselbe Fallback-Logik wie backend/src/domain/userDisplay.ts -- Frontend/
// Backend sind unabhängige npm-Pakete ohne geteilte Bibliothek, daher dupliziert.
function composeFullName(claims: DecodedClaims | undefined): string | undefined {
  const given = claims?.givenName?.trim();
  const family = claims?.familyName?.trim();
  if (!given || !family) return undefined;
  return `${given} ${family}`;
}

function rolesFromClaims(claims: DecodedClaims | undefined): Role[] {
  if (!claims) return [];
  const roles: Role[] = [];
  if (claims.groups.includes('admins')) roles.push('admin');
  if (claims.groups.includes('abteilungsleiter')) roles.push('abteilungsleiter');
  if (claims.groups.includes('uebungsleiter')) roles.push('uebungsleiter');
  return roles;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [idToken, setIdToken] = useState<string>();
  const [claims, setClaims] = useState<DecodedClaims>();

  const refresh = useCallback(async () => {
    const token = await getCurrentIdToken();
    setIdToken(token);
    setClaims(token ? decodeIdToken(token) : undefined);
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Another tab signing out (or in) writes to localStorage; react to it so a
  // stale, already-signed-out session never keeps showing protected content.
  useEffect(() => {
    const onStorage = () => void refresh();
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [refresh]);

  const logout = useCallback(() => {
    cognitoLogout();
    setIdToken(undefined);
    setClaims(undefined);
  }, []);

  const roles = rolesFromClaims(claims);
  const hasRole = (role: Role) => roles.includes(role);
  const fullName = composeFullName(claims);

  return (
    <AuthContext.Provider
      value={{
        loading,
        idToken,
        claims,
        roles,
        hasRole,
        fullName,
        hasCompleteName: Boolean(fullName),
        refresh,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- hook colocated with its provider
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
